/**
 * A mecânica do portão de identidade do inscrito — sem React.
 *
 * O portão tem quatro passos (CPF → canal → código → senha) e um desvio pelo
 * gov.br. Quem MANDA nas regras é a API (F04): validade do código, espera
 * entre reenvios, teto de reenvios, tentativas e bloqueio são decididos no
 * banco, e cada resposta traz o estado do fluxo (`EstadoDoFluxo`) com os
 * prazos. Esta máquina guarda o que só a tela sabe — o que foi digitado, o
 * passo à vista, a orientação aberta — e transforma os prazos do servidor em
 * contagem regressiva.
 *
 * Duas decisões que valem ler antes de mexer:
 *
 * 1. **O relógio entra por parâmetro.** Nenhuma função aqui chama
 *    `Date.now()`: quem despacha informa o instante. Teste com relógio real é
 *    lento e intermitente — e este repositório já perdeu PR por data presa.
 * 2. **O relógio do aparelho não é o do servidor.** Cada estado do fluxo vem
 *    com o `agora` do servidor; a diferença para o relógio local vira
 *    `desvioMs`, e todo prazo é contado no relógio do servidor. Sem isso, um
 *    celular adiantado dois minutos mostraria "código vencido" com código
 *    válido.
 */

import { somenteDigitos } from '../contrato';
import { type CanalDoCodigo, type EstadoDoFluxo, type ViaDoFluxo } from './api';

/** Quantos dígitos tem o código. */
export const DIGITOS_DO_CODIGO = 6;

/** Os passos do portão, na ordem em que aparecem. `govbr` é o desvio. */
export const PASSOS_DA_IDENTIDADE = ['cpf', 'canal', 'codigo', 'senha'] as const;
export type PassoDaIdentidade = (typeof PASSOS_DA_IDENTIDADE)[number] | 'govbr';

/**
 * Os becos sem saída, que viram orientação em vez de erro técnico (CLAUDE.md,
 * "Regras de interface", item 8).
 *
 * `PROCURAR_DTP` é UMA orientação para "CPF sem cadastro" e "sem contato": a
 * API responde igual nos dois casos, para ninguém descobrir por tentativa qual
 * CPF está no cadastro (RN-CP-10). `SGTP_INDISPONIVEL` é outra coisa — falha
 * passageira, que pede tentar de novo, não ir ao balcão.
 */
export const MOTIVOS_DE_ORIENTACAO = [
  'PROCURAR_DTP',
  'EMAIL_DESATUALIZADO',
  'TELEFONE_DESATUALIZADO',
  'SGTP_INDISPONIVEL',
  'GOVBR_RECUSADO',
  'GOVBR_INDISPONIVEL',
] as const;
export type MotivoDeOrientacao = (typeof MOTIVOS_DE_ORIENTACAO)[number];

export interface EstadoDaIdentidade {
  passo: PassoDaIdentidade;
  /** Só dígitos. A máscara é assunto da tela. */
  cpf: string;
  canal: CanalDoCodigo;
  codigo: string;
  /** O que a API disse por último. Nulo antes do primeiro passo concluído. */
  fluxo: EstadoDoFluxo | null;
  orientacao: MotivoDeOrientacao | null;
  /** Frase para mostrar junto do passo (código incorreto, espere, etc.). */
  aviso: string | null;
  /** Uma chamada em curso: botões ficam indisponíveis. */
  ocupado: boolean;
  /** Relógio local, alimentado de fora. */
  agora: number;
  /** Relógio do servidor menos o local, em milissegundos. */
  desvioMs: number;
}

export type AcaoDaIdentidade =
  | { tipo: 'tique'; agora: number }
  | { tipo: 'digitar_cpf'; cpf: string }
  | { tipo: 'digitar_codigo'; codigo: string }
  | { tipo: 'escolher_canal'; canal: CanalDoCodigo }
  | { tipo: 'ir_para'; passo: PassoDaIdentidade }
  | { tipo: 'ocupar' }
  | { tipo: 'avisar'; aviso: string | null }
  | {
      tipo: 'receber_fluxo';
      fluxo: EstadoDoFluxo;
      agora: number;
      aviso?: string | null;
      /** Força o passo; sem ele, o passo segue a etapa do fluxo. */
      passo?: PassoDaIdentidade;
    }
  | { tipo: 'aguardar_govbr' }
  | { tipo: 'orientar'; motivo: MotivoDeOrientacao }
  | { tipo: 'dispensar_orientacao' };

/**
 * O que os painéis pedem ao portão. As chamadas à API moram no portão, e não
 * nos painéis, para o tratamento de erro ser um só.
 */
export interface AcoesDoPortao {
  continuarComCpf: () => Promise<void>;
  enviarCodigo: () => Promise<void>;
  conferirCodigo: () => Promise<void>;
  /** Devolve os requisitos que faltam, ou nulo quando a senha foi aceita. */
  definirSenha: (senha: string, confirmacao: string) => Promise<string[] | 'DIFERENTE' | null>;
  entrarSemTrocarSenha: () => Promise<void>;
  iniciarGovbr: () => Promise<void>;
  confirmarGovbr: () => Promise<void>;
}

/** O que cada painel de passo recebe. */
export interface PropsDoPassoDaIdentidade {
  estado: EstadoDaIdentidade;
  despachar: (acao: AcaoDaIdentidade) => void;
  acoes: AcoesDoPortao;
}

export function estadoInicialDaIdentidade(agora = 0): EstadoDaIdentidade {
  return {
    passo: 'cpf',
    cpf: '',
    canal: 'EMAIL',
    codigo: '',
    fluxo: null,
    orientacao: null,
    aviso: null,
    ocupado: false,
    agora,
    desvioMs: 0,
  };
}

/* ────────────────────────────── As leituras ─────────────────────────────── */

function agoraNoServidor(estado: EstadoDaIdentidade): number {
  return estado.agora + estado.desvioMs;
}

function segundosAte(instante: string | null | undefined, estado: EstadoDaIdentidade): number {
  if (!instante) return 0;
  const alvo = Date.parse(instante);
  if (Number.isNaN(alvo)) return 0;
  return Math.max(0, Math.ceil((alvo - agoraNoServidor(estado)) / 1000));
}

/** Quanto falta para o código vencer, em segundos. Zero quando não há código. */
export function segundosDoCodigo(estado: EstadoDaIdentidade): number {
  return segundosAte(estado.fluxo?.codigo?.expiraEm, estado);
}

/** Verdadeiro quando já houve envio e o prazo do código acabou. */
export function codigoExpirou(estado: EstadoDaIdentidade): boolean {
  return Boolean(estado.fluxo?.codigo) && segundosDoCodigo(estado) === 0;
}

/**
 * O contato mascarado para onde o código foi, para a frase "Enviamos um código
 * para …". O do código, quando a API o trouxe; senão, o do canal escolhido na
 * lista de contatos do mesmo fluxo — a frase nunca fica com o destino vazio.
 * Nulo só quando nenhum dos dois existe.
 */
export function destinoDoCodigo(estado: EstadoDaIdentidade): string | null {
  const doCodigo = estado.fluxo?.codigo?.destino?.trim();
  if (doCodigo) return doCodigo;
  const canal = estado.fluxo?.codigo?.canal ?? estado.canal;
  const doCanal = estado.fluxo?.canais.find((c) => c.canal === canal)?.destino?.trim();
  return doCanal || null;
}

/** Quanto falta da espera entre reenvios, em segundos. */
export function segundosDeEspera(estado: EstadoDaIdentidade): number {
  return segundosAte(estado.fluxo?.codigo?.reenvioLiberadoEm, estado);
}

/** Quanto falta do bloqueio, em segundos. */
export function segundosDeBloqueio(estado: EstadoDaIdentidade): number {
  return segundosAte(estado.fluxo?.codigo?.bloqueadoAte, estado);
}

export function estaBloqueado(estado: EstadoDaIdentidade): boolean {
  return segundosDeBloqueio(estado) > 0;
}

/** O CPF tem os onze dígitos. O dígito verificador é conferido pela API. */
export function cpfEstaCompleto(estado: EstadoDaIdentidade): boolean {
  return somenteDigitos(estado.cpf).length === 11;
}

export function reenviosRestantes(estado: EstadoDaIdentidade): number {
  return estado.fluxo?.codigo?.reenviosRestantes ?? 0;
}

export function tentativasRestantes(estado: EstadoDaIdentidade): number {
  return estado.fluxo?.codigo?.tentativasRestantes ?? 0;
}

/** O botão de reenviar fica disponível. A API confere de novo — isto só evita o clique inútil. */
export function podeReenviarCodigo(estado: EstadoDaIdentidade): boolean {
  if (!estado.fluxo?.codigo || estado.ocupado) return false;
  if (estaBloqueado(estado)) return false;
  if (reenviosRestantes(estado) <= 0) return false;
  return segundosDeEspera(estado) === 0;
}

/** `09:59`, do jeito que o cronômetro aparece na tela. */
export function emMinutosESegundos(segundos: number): string {
  const seguro = Math.max(0, Math.floor(segundos));
  const minutos = Math.floor(seguro / 60);
  const resto = seguro % 60;
  return `${String(minutos).padStart(2, '0')}:${String(resto).padStart(2, '0')}`;
}

/** O rótulo do botão de reenviar — a única pista de por que ele não responde. */
export function rotuloDoReenvio(estado: EstadoDaIdentidade): string {
  if (estado.fluxo?.codigo && reenviosRestantes(estado) <= 0) return 'Limite de reenvios atingido';
  if (estaBloqueado(estado))
    return `Bloqueado por ${emMinutosESegundos(segundosDeBloqueio(estado))}`;
  const espera = segundosDeEspera(estado);
  if (espera > 0) return `Aguarde ${emMinutosESegundos(espera)}`;
  return 'Reenviar código';
}

/** Como a identidade foi comprovada — é o que a tarja do assistente mostra. */
export type ViaDaIdentidade = 'GOVBR' | 'EMAIL' | 'SMS';

/** A via, depois que a API confirmou. */
export function viaDaIdentidade(estado: EstadoDaIdentidade): ViaDaIdentidade | null {
  const via: ViaDoFluxo | null | undefined = estado.fluxo?.via;
  if (!via) return null;
  if (via === 'GOVBR') return 'GOVBR';
  return estado.fluxo?.codigo?.canal ?? estado.canal;
}

/* ─────────────────────────────── O redutor ──────────────────────────────── */

function passoDaEtapa(fluxo: EstadoDoFluxo): PassoDaIdentidade {
  if (fluxo.etapa === 'SENHA') return 'senha';
  if (fluxo.etapa === 'CODIGO') return 'codigo';
  return 'canal';
}

/**
 * A regra do portão inteira, num lugar só. Ação recusada devolve o MESMO
 * objeto — o teste compara por identidade.
 */
export function reduzirIdentidade(
  estado: EstadoDaIdentidade,
  acao: AcaoDaIdentidade,
): EstadoDaIdentidade {
  switch (acao.tipo) {
    case 'tique':
      return acao.agora === estado.agora ? estado : { ...estado, agora: acao.agora };

    case 'digitar_cpf':
      return { ...estado, cpf: somenteDigitos(acao.cpf).slice(0, 11), aviso: null };

    case 'digitar_codigo':
      return { ...estado, codigo: somenteDigitos(acao.codigo).slice(0, DIGITOS_DO_CODIGO) };

    case 'escolher_canal':
      return acao.canal === estado.canal ? estado : { ...estado, canal: acao.canal };

    case 'ir_para':
      return acao.passo === estado.passo ? estado : { ...estado, passo: acao.passo, aviso: null };

    case 'ocupar':
      return estado.ocupado ? estado : { ...estado, ocupado: true, aviso: null };

    case 'avisar':
      return { ...estado, ocupado: false, aviso: acao.aviso };

    case 'receber_fluxo': {
      const servidor = Date.parse(acao.fluxo.agora);
      const canais = acao.fluxo.canais.map((c) => c.canal);
      // O canal escolhido só vale se o SGTP o tiver; senão, o primeiro que houver.
      const canal =
        acao.fluxo.codigo?.canal ??
        (canais.includes(estado.canal) ? estado.canal : (canais[0] ?? estado.canal));
      return {
        ...estado,
        fluxo: acao.fluxo,
        passo: acao.passo ?? passoDaEtapa(acao.fluxo),
        canal,
        // Código novo, campo limpo: o que estava digitado era do anterior.
        codigo: acao.fluxo.codigo?.expiraEm !== estado.fluxo?.codigo?.expiraEm ? '' : estado.codigo,
        aviso: acao.aviso ?? null,
        ocupado: false,
        orientacao: null,
        agora: acao.agora,
        desvioMs: Number.isNaN(servidor) ? estado.desvioMs : servidor - acao.agora,
      };
    }

    case 'aguardar_govbr':
      return { ...estado, passo: 'govbr', ocupado: false, aviso: null, orientacao: null };

    case 'orientar':
      return { ...estado, orientacao: acao.motivo, ocupado: false, aviso: null };

    case 'dispensar_orientacao':
      return estado.orientacao === null ? estado : { ...estado, orientacao: null };

    default:
      return estado;
  }
}

/* ────────────────────── As frases de cada resultado ─────────────────────── */

export const AVISO_DO_ENVIO: Record<string, string | null> = {
  ENVIADO: null,
  JA_VERIFICADO: null,
  AGUARDE_REENVIO: 'Espere o tempo indicado para pedir outro código.',
  REENVIOS_ESGOTADOS:
    'Você atingiu o limite de reenvios. Comece de novo informando o CPF, ou entre com o gov.br.',
  BLOQUEADO: 'A conferência está bloqueada. Espere o prazo indicado para tentar de novo.',
  CANAL_INDISPONIVEL: 'Esse canal não está no cadastro do DTP. Escolha outro.',
};

export function avisoDaConferencia(resultado: string, tentativas: number): string | null {
  switch (resultado) {
    case 'CONFERIDO':
      return null;
    case 'INCORRETO':
      return tentativas === 1
        ? 'Código incorreto. Resta 1 tentativa antes do bloqueio.'
        : `Código incorreto. Restam ${tentativas} tentativas antes do bloqueio.`;
    case 'EXPIRADO':
      return 'O código venceu. Peça um novo abaixo.';
    case 'BLOQUEADO':
      return 'Você errou o código muitas vezes. A conferência ficou bloqueada por alguns minutos.';
    default:
      return 'Peça um código antes de confirmar.';
  }
}
